/**
 * Motion Precedence and Stack Management System
 * Handles proper ordering and interaction of parliamentary motions
 */

import { MOTION_TYPES, MOTION_CLASSES, getMotionPrecedence, canMotionInterrupt, isMotionInOrder } from './motionTypes.js';
import { getTopPendingMotion } from './ronrRanks.js';

/**
 * Insert a motion into the motion stack based on precedence rules
 * @param {Array} currentStack - Current motion stack
 * @param {Object} newMotion - Motion to insert
 * @returns {Array} Updated motion stack with proper precedence ordering
 */
export const insertMotionByPrecedence = (currentStack, newMotion) => {
  const newMotionPrecedence = getMotionPrecedence(newMotion.type);
  const newStack = [...currentStack];

  // Find the correct insertion point based on precedence
  let insertIndex = 0;
  for (let i = 0; i < newStack.length; i++) {
    const stackMotionPrecedence = getMotionPrecedence(newStack[i].type);

    // Insert before the first motion with lower precedence (higher number)
    if (newMotionPrecedence < stackMotionPrecedence) {
      insertIndex = i;
      break;
    }
    insertIndex = i + 1;
  }

  newStack.splice(insertIndex, 0, newMotion);
  return newStack;
};

/**
 * Check if a new motion can be applied to the current parliamentary state
 * @param {string} motionType - Type of motion to check
 * @param {Object} currentMotion - Currently pending motion
 * @param {Array} motionStack - Current motion stack
 * @returns {Object} {canApply: boolean, reason: string}
 */
export const canApplyMotion = (motionType, currentMotion, motionStack) => {
  const motion = MOTION_TYPES[motionType];
  if (!motion) {
    return { canApply: false, reason: 'Unknown motion type' };
  }

  // Check if motion is in order based on current state
  if (!isMotionInOrder(motionType, currentMotion, motionStack)) {
    return { canApply: false, reason: getNotInOrderReason(motionType, currentMotion, motionStack) };
  }

  // Check for conflicting motions already on the stack
  const conflict = findMotionConflict(motionType, motionStack);
  if (conflict) {
    return { canApply: false, reason: conflict };
  }

  // Check specific motion application rules
  const specificCheck = checkSpecificMotionRules(motionType, currentMotion, motionStack);
  if (!specificCheck.canApply) {
    return specificCheck;
  }

  return { canApply: true, reason: 'Motion is in order' };
};

/**
 * Get reason why a motion is not in order
 */
const getNotInOrderReason = (motionType, currentMotion, motionStack) => {
  const motion = MOTION_TYPES[motionType];

  switch (motion.class) {
    case MOTION_CLASSES.MAIN:
      if (currentMotion || motionStack.length > 0) {
        return 'Main motions not in order when business is pending';
      }
      break;

    case MOTION_CLASSES.BRING_BACK:
      if (currentMotion || motionStack.length > 0) {
        return 'This motion is only in order when no question is pending';
      }
      break;

    case MOTION_CLASSES.SUBSIDIARY:
      if (motionType === 'AMEND') {
        const amendmentCount =
          (currentMotion?.type === 'AMEND' ? 1 : 0) +
          motionStack.filter((m) => m.type === 'AMEND').length;
        if (amendmentCount >= 2) {
          return 'Cannot amend an amendment to an amendment (only two degrees are allowed)';
        }
        return 'Amendments must be applied to an amendable pending question';
      }
      const hasMainMotion = currentMotion?.type === 'MAIN' ||
                           motionStack.some(m => MOTION_TYPES[m.type]?.class === MOTION_CLASSES.MAIN);
      if (!hasMainMotion) {
        return 'Subsidiary motions require a pending main motion';
      }
      break;

    case MOTION_CLASSES.PRIVILEGED:
      if (!getTopPendingMotion(currentMotion, motionStack)) {
        return 'When no question is pending, only a main motion is in order';
      }
      break;

    case MOTION_CLASSES.INCIDENTAL:
      return getIncidentalMotionNotInOrderReason(motionType, currentMotion, motionStack);
  }

  return 'Motion not in order';
};

/**
 * Get specific reason why an incidental motion is not in order.
 * The authoritative in-order logic lives in motionTypes.js `isMotionInOrder`; this only
 * produces the human-readable explanation shown when a motion is blocked.
 */
const getIncidentalMotionNotInOrderReason = (motionType, currentMotion, motionStack) => {
  switch (motionType) {
    case 'WITHDRAW_MOTION':
      if (!currentMotion) {
        return 'No motion pending to withdraw';
      }
      if (currentMotion.status === 'voted') {
        return 'Cannot withdraw motion after voting';
      }
      break;

    case 'DIVISION_QUESTION':
      if (!currentMotion) {
        return 'No motion pending to divide';
      }
      break;

    case 'DIVISION_ASSEMBLY':
      if (!currentMotion) {
        return 'A division may be demanded only while a question is pending or just after a voice vote';
      }
      break;

    case 'OBJECT_CONSIDERATION':
      if (!currentMotion || currentMotion.type !== 'MAIN') {
        return 'Can only object to consideration of main motions';
      }
      if (currentMotion.status !== 'stated' && currentMotion.status !== 'open_debate') {
        return 'Must object before debate proceeds';
      }
      break;
  }

  return 'Incidental motion not in order';
};

/**
 * Find conflicting motions on the stack
 */
const findMotionConflict = (motionType, motionStack) => {
  // Check for specific motion conflicts
  const conflicts = {
    'TABLE': ['POSTPONE', 'COMMIT'], // Can't table and postpone/refer
    'POSTPONE': ['TABLE', 'COMMIT'], // Can't postpone and table/refer
    'COMMIT': ['TABLE', 'POSTPONE'], // Can't refer and table/postpone
    'PREVQ': ['LIMIT_DEBATE'], // Previous question conflicts with debate limits
    'LIMIT_DEBATE': ['PREVQ']
  };

  const conflictingTypes = conflicts[motionType];
  if (conflictingTypes) {
    for (const conflictType of conflictingTypes) {
      if (motionStack.some(m => m.type === conflictType)) {
        const conflictingMotion = MOTION_TYPES[conflictType];
        return `Conflicts with pending ${conflictingMotion?.name || conflictType} motion`;
      }
    }
  }

  return null;
};

/**
 * Check specific application rules for individual motions
 */
const checkSpecificMotionRules = (motionType, currentMotion, motionStack) => {
  switch (motionType) {
    case 'AMEND': {
      // Can't amend an amendment to an amendment (3rd degree). Count both the
      // immediately pending motion and everything held on the stack.
      const amendmentCount =
        (currentMotion?.type === 'AMEND' ? 1 : 0) +
        motionStack.filter(m => m.type === 'AMEND').length;
      if (amendmentCount >= 2) {
        return { canApply: false, reason: 'Cannot amend an amendment to an amendment (3rd degree)' };
      }
      break;
    }

    case 'POSTPONE_INDEF':
      // Postpone indefinitely can only be applied to main motions
      const hasMainMotion = currentMotion?.type === 'MAIN' ||
                           motionStack.some(m => MOTION_TYPES[m.type]?.class === MOTION_CLASSES.MAIN);
      if (!hasMainMotion) {
        return { canApply: false, reason: 'Postpone indefinitely only applies to main motions' };
      }
      break;

    case 'OBJECT_CONSIDERATION':
      // Must be applied to main motion only
      if (currentMotion?.type !== 'MAIN') {
        return { canApply: false, reason: 'Can only object to consideration of main motions' };
      }
      break;
  }

  return { canApply: true };
};

/**
 * Determine what motion should be immediately pending (actively being considered)
 * @param {Array} motionStack - Current motion stack
 * @returns {Object|null} The motion that should be immediately pending
 */
export const getImmediatelyPendingMotion = (motionStack) => {
  if (motionStack.length === 0) return null;

  // The motion with the highest precedence (lowest precedence number) is immediately pending
  let immediatelyPending = motionStack[0];
  let highestPrecedence = getMotionPrecedence(immediatelyPending.type);

  for (let i = 1; i < motionStack.length; i++) {
    const motionPrecedence = getMotionPrecedence(motionStack[i].type);
    if (motionPrecedence < highestPrecedence) {
      immediatelyPending = motionStack[i];
      highestPrecedence = motionPrecedence;
    }
  }

  return immediatelyPending;
};

/**
 * Remove a motion from the stack and return updated stack
 * @param {Array} motionStack - Current motion stack
 * @param {string} motionId - ID of motion to remove
 * @returns {Array} Updated motion stack
 */
export const removeMotionFromStack = (motionStack, motionId) => {
  return motionStack.filter(motion => motion.id !== motionId);
};

/**
 * Get all motions that would be affected by the passage/failure of a given motion
 * @param {Object} motion - The motion being resolved
 * @param {Array} motionStack - Current motion stack
 * @returns {Array} Array of affected motions
 */
export const getAffectedMotions = (motion, motionStack) => {
  const affected = [];

  switch (motion.type) {
    case 'TABLE':
    case 'POSTPONE':
    case 'COMMIT':
    case 'POSTPONE_INDEF':
      // These dispose of the main question and all subsidiary motions
      affected.push(...motionStack);
      break;

    case 'PREVQ':
      // Previous question affects all debatable motions
      affected.push(...motionStack.filter(m => MOTION_TYPES[m.type]?.debatable));
      break;

    case 'AMEND':
      // Amendment only affects the motion being amended
      if (motion.targetMotionId) {
        const targetMotion = motionStack.find(m => m.id === motion.targetMotionId);
        if (targetMotion) affected.push(targetMotion);
      }
      break;
  }

  return affected;
};